import { AuthState } from "../base/AuthState";

import welcomeTemplate from "./SignupWelcomeTemplate.html";
import benefitsTemplate from "./SignupBenefitsTemplate.html";
import registerTemplate from "./SignupRegisterTemplate.html";
import doneTemplate from "./SignupDoneTemplate.html";

import { _lcs, currentLanguage } from "../../i18n";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const TOTAL_STEPS = 3;

const STEP_WELCOME = 0;
const STEP_BENEFITS = 1;
const STEP_REGISTER = 2;

// What the app is for, shown on step 2. German source strings double as their
// dictionary keys; the template translates them (this constant is evaluated
// before I18n exists).
const BENEFITS = [
    {
        icon : "calendar-days",
        title : "Events & Erlebnisse",
        text : "Weinabend, Sonnenaufgangstour, Konzert im Fels — alles im Haus und in der Region, mit einem Tipp gebucht."
    },
    {
        icon : "map-pinned",
        title : "Region-Tipps",
        text : "Restaurants, Sehenswürdigkeiten und Touren in deiner Nähe — als Liste oder auf der Karte."
    },
    {
        icon : "route",
        title : "Dein Planer",
        text : "Sammle alles, was dich interessiert, und ordne deine Tage so, wie es dir passt."
    },
    {
        icon : "message-circle",
        title : "Direkt verbunden",
        text : "Schreib deinen Gastgebern jederzeit — oder frag den Concierge, wenn es schnell gehen soll."
    }
];

/**
 * SignupState — create a guest account, as a three-step flow.
 *
 *   1. Welcome — full-bleed region hero with the house lockup.
 *   2. Benefits — what the app is for, before asking for anything.
 *   3. Register — the account form.
 *
 * Each step is its own template and the state re-renders on every transition,
 * so what is on screen is always exactly one step's markup. Typed values are
 * lifted into `this.form` before a re-render and read back into the template —
 * going back to re-read the benefits must not cost the guest their input.
 *
 * The register endpoint is not wired yet: submitting validates locally and
 * enters the app as a guest, which keeps the whole flow navigable.
 */
export class SignupState extends AuthState
{
    static ROUTE = "/signup";
    static ID = "signup";

    async enter()
    {
        this.step = STEP_WELCOME;

        // Survives the re-render between steps.
        this.form = {
            firstname : "",
            lastname : "",
            email : "",
            newsletter : true,
            agree : false
        };

        this.renderStep();
    }

    /* -------------------------------- Steps --------------------------------- */

    goToStep( step )
    {
        // Only the register step holds input, but capturing unconditionally
        // keeps the transition logic free of "which step were we on" branches.
        this.captureForm();
        this.step = step;
        this.renderStep();
    }

    renderStep()
    {
        if ( this.step === STEP_WELCOME )
        {
            this.renderWelcome();
        }
        else if ( this.step === STEP_BENEFITS )
        {
            this.renderBenefits();
        }
        else
        {
            this.renderRegister();
        }

        this.paintProgress();
    }

    renderWelcome()
    {
        this.renderAuth( welcomeTemplate, {
            house : this._houseName(),
            region : this._region(),
            logoUrl : this.app.accommodation?.theme?.logoUrl || null
        } );

        this.bind( "next", () => this.goToStep( STEP_BENEFITS ) );
        this.bindToLogin();
    }

    renderBenefits()
    {
        this.renderAuth( benefitsTemplate, { benefits : BENEFITS } );

        this.bind( "back", () => this.goToStep( STEP_WELCOME ) );
        this.bind( "next", () => this.goToStep( STEP_REGISTER ) );
        // "Überspringen" skips the tour, not the registration — it is the last
        // step that actually asks for something.
        this.bind( "skip", () => this.goToStep( STEP_REGISTER ) );
    }

    renderRegister()
    {
        this.renderAuth( registerTemplate, { form : this.form } );

        this.bind( "back", () => this.goToStep( STEP_BENEFITS ) );
        this.bindToLogin();
        this.bindPasswordToggle();
        this.bindAgree();
        this.bindForm();
    }

    /**
     * The step dots. Built here rather than in each template because all three
     * steps show the same control and only the index and tone differ.
     */
    paintProgress()
    {
        const host = document.querySelector( '[data-id="progress"]' );
        if ( !host ) return;

        host.setAttribute( "role", "progressbar" );
        host.setAttribute( "aria-valuemin", "1" );
        host.setAttribute( "aria-valuemax", String( TOTAL_STEPS ) );
        host.setAttribute( "aria-valuenow", String( this.step + 1 ) );
        host.setAttribute( "aria-label", _lcs( "Schritt {step} von {total}", {
            step : this.step + 1, total : TOTAL_STEPS
        } ) );

        host.innerHTML = Array.from( { length: TOTAL_STEPS } )
            .map( ( _, i ) =>
                '<span class="onb-progress__dot' +
                ( i === this.step ? " onb-progress__dot--active" : "" ) +
                '"></span>' )
            .join( "" );
    }

    /* ------------------------------- Bindings -------------------------------- */

    bind( id, handler )
    {
        const el = document.querySelector( '[data-id="' + id + '"]' );
        if ( el ) this.addEventListener( el, "click", handler );
    }

    bindToLogin()
    {
        const el = document.querySelector( '[data-id="to-login"]' );
        if ( !el ) return;
        this.addEventListener( el, "click", ( e ) =>
        {
            e.preventDefault();
            this.app.router.redirect( "/login" );
        } );
    }

    bindAgree()
    {
        const agree = document.querySelector( '[data-id="agree"]' );
        const create = document.querySelector( '[data-id="create"]' );
        if ( !agree || !create ) return;

        const sync = () => { create.disabled = !agree.checked; };
        sync();
        this.addEventListener( agree, "change", sync );
    }

    bindPasswordToggle()
    {
        const toggle = document.querySelector( '[data-id="toggle-pw"]' );
        const pw = document.getElementById( "su-password" );
        if ( !toggle || !pw ) return;

        const icon = toggle.querySelector( ".icon" );
        this.addEventListener( toggle, "click", () =>
        {
            const show = pw.type === "password";
            pw.type = show ? "text" : "password";
            toggle.setAttribute( "aria-pressed", String( show ) );
            toggle.setAttribute( "aria-label",
                show ? _lcs( "Passwort verbergen" ) : _lcs( "Passwort anzeigen" ) );
            if ( icon )
            {
                icon.classList.toggle( "icon--eye", !show );
                icon.classList.toggle( "icon--eye-off", show );
            }
            pw.focus();
        } );
    }

    bindForm()
    {
        const form = document.getElementById( "signup-form" );
        if ( !form ) return;

        const errorEl = form.querySelector( '[data-id="signup-error"]' );
        const submitBtn = document.querySelector( '[data-id="create"]' );

        this.addEventListener( form, "submit", async ( e ) =>
        {
            e.preventDefault();
            this._clearError( errorEl );
            this.captureForm();

            const name = [ this.form.firstname, this.form.lastname ].filter( Boolean ).join( " " ).trim();
            if ( !name )
            {
                return this._showError( errorEl, _lcs( "Bitte gib deinen Namen ein." ) );
            }
            if ( !EMAIL_RE.test( this.form.email ) )
            {
                return this._showError( errorEl, _lcs( "Bitte gib eine gültige E-Mail-Adresse ein." ) );
            }

            const password = document.getElementById( "su-password" )?.value || "";
            if ( password.length < 8 )
            {
                return this._showError( errorEl, _lcs( "Das Passwort braucht mindestens 8 Zeichen." ) );
            }

            this._setLoading( submitBtn, true );
            try
            {
                const payload = {
                    name : name,
                    email : this.form.email,
                    password : password,
                    // The validation mail is written in this language …
                    language : currentLanguage()
                };
                // … and its link leads back to this house.
                const slug = this.app.theme.getTenantSlug();
                if ( slug ) payload.slug = slug;

                const { result } = await this.app.api.post( "/auth/user", payload );

                if ( !result || +result.status !== 201 )
                {
                    throw new Error( "Unexpected signup response." );
                }

                this._showConfirmation();
            }
            catch ( err )
            {
                console.error( err );
                this._setLoading( submitBtn, false );
                this._showError( errorEl, this._errorMessageForStatus( +err?.status ) );
            }
        } );
    }

    /**
     * What a guest sees the moment the account exists.
     *
     * A confirmation screen rather than a toast: the account is created but
     * NOT yet usable — it is `not_validated` until the mailed link is opened —
     * and dropping the guest straight into the app would suggest otherwise.
     */
    _showConfirmation()
    {
        this.renderAuth( doneTemplate, { email : this.form.email } );
        this.paintProgress();

        this.bind( "to-login", () => this.app.router.redirect( "/login" ) );
        this.bind( "guest", () => this.app.enterAsGuest() );
        this.bind( "resend", ( e ) =>
        {
            e.preventDefault();
            this._resendValidation();
        } );
    }

    /**
     * "Link erneut senden" on the confirmation: a fresh validation mail (the
     * earlier link stops working). The API answers 200 whatever happens and
     * sends at most one mail a minute, so the receipt is the same every time.
     */
    async _resendValidation()
    {
        if ( this.resending )
        {
            return;
        }
        this.resending = true;
        try
        {
            const payload = { email : this.form.email, language : currentLanguage() };
            const slug = this.app.theme.getTenantSlug();
            if ( slug ) payload.slug = slug;

            await this.app.api.post( "/auth/user/validation-mail", payload );
            this.app.view.toast( _lcs( "Wir haben dir einen neuen Link an {email} geschickt.", { email : this.form.email } ), "success", 5000 );
        }
        catch ( err )
        {
            console.error( err );
            this.app.view.toast( _lcs( "Der Link konnte nicht angefordert werden. Bitte versuche es später erneut." ), "error" );
        }
        this.setTimeout( () => { this.resending = false; }, 30000 );
    }

    _errorMessageForStatus( status )
    {
        switch ( status )
        {
            // The address is taken — the one case with a real next step.
            case 409: return _lcs( "Für diese E-Mail-Adresse gibt es schon ein Konto. " +
                                   "Bitte melde dich stattdessen an." );
            case 400: return _lcs( "Bitte überprüfe deine Eingaben." );
            default:  return _lcs( "Die Registrierung hat nicht geklappt. Bitte versuche es später erneut." );
        }
    }

    _setLoading( btn, on )
    {
        if ( !btn ) return;
        btn.disabled = on;
        btn.setAttribute( "aria-busy", String( on ) );
    }

    /**
     * Lift the register step's values into `this.form`. The password is
     * deliberately not kept: re-rendering it into a `value=` attribute would
     * write the guest's password into the DOM for the rest of the session.
     */
    captureForm()
    {
        const value = ( id ) => document.getElementById( id )?.value ?? null;
        const checked = ( id ) => document.querySelector( '[data-id="' + id + '"]' )?.checked ?? null;

        const firstname = value( "su-firstname" );
        const lastname = value( "su-lastname" );
        const email = value( "su-email" );
        const newsletter = checked( "newsletter" );
        const agree = checked( "agree" );

        if ( firstname !== null ) this.form.firstname = firstname.trim();
        if ( lastname !== null ) this.form.lastname = lastname.trim();
        if ( email !== null ) this.form.email = email.trim();
        if ( newsletter !== null ) this.form.newsletter = newsletter;
        if ( agree !== null ) this.form.agree = agree;
    }

    /* -------------------------------- Helpers -------------------------------- */

    _houseName()
    {
        const slug = this.app.theme.getTenantSlug();
        if ( !slug )
        {
            return "Gästehaus Felsenblick";
        }
        return slug
            .split( "-" )
            .map( ( w ) => w.charAt( 0 ).toUpperCase() + w.slice( 1 ) )
            .join( " " );
    }

    _region()
    {
        return _lcs( "Sächsische Schweiz" );
    }

    _showError( el, message )
    {
        if ( !el ) return;
        el.textContent = message;
        el.hidden = false;
    }

    _clearError( el )
    {
        if ( !el ) return;
        el.textContent = "";
        el.hidden = true;
    }

    async exit() {}
}
